import { Injectable, Inject, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import uuid from 'short-unique-id';
import { ToastrService } from 'ngx-toastr';
import { GlobalService } from 'app/global/global.service';
import { PopOver } from '../popover/popover';
import feather from 'feather-icons';
import { TranslocoService } from '@jsverse/transloco';
import { ElementPosition } from '../content-tab/content-tab.dto';

@Injectable({
    providedIn: 'root'
})
export class Blocks {
    public uuid = new uuid({ length: 6, dictionary: 'number' });
    private renderer: Renderer2;
    constructor(
        private toastr: ToastrService,
        private globalService: GlobalService,
        private PopOver: PopOver,
        private _translocoService: TranslocoService,
        @Inject(Injector) private readonly injector: Injector,
        rendererFactory: RendererFactory2
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    allowedSelf() {
        return this;
    }
    /**
     * create a content box html view for editor
     * @param params 
     */
    createContentBox(params) {
        const that = this.allowedSelf();
        that.toastr.toastrConfig.preventDuplicates = true;
        const uuid = this.uuid();
        const editorinstance = document.createElement('div')
        editorinstance.className = 'content-box content-box-block content-editor-block marginminus70'
        editorinstance.innerHTML = `<div class="sort-helper"></div>
        <div class="content-box-row">
                <div class="rowItem contentbox">
                ${that.PopOver.listingPopover({ classType: 'content-box', uuid: uuid })}
                <div class="content-box-inner active-border" editor-uuid="${uuid}" type="content box"><div class="editor${uuid}  cke-editor-box editor-word-wrap" editor-uuid="${uuid}" contenteditable="false" cke-placeholder="${this._translocoService.translate("Content block html text on content-box (Template/Document - section) - Type / for quick actions")}">
                </div>
            </div>
        </div>
        </div>`;
        const indicator = document.querySelector('.content-box-enter')?.querySelector('[class^="dropIndicator-"]') as HTMLElement;
        const templatePage = document.querySelector('.template-page:not([hidden])');
        if (!document.querySelector('.dropIndicator-bottom') && templatePage?.classList.contains('addContentBox') && params?.type !== 'slashcommand') {
            this.renderer.appendChild(templatePage, editorinstance);
        } else if (params?.editor && params?.type === 'slashcommand') {
            params.editor.view.dom.closest('.content-box').insertAdjacentElement('afterend', editorinstance);
        } else if(indicator?.classList?.contains('dropIndicator-inner-bottom')){
            this.globalService.onDropOnInnerIndicator(indicator, editorinstance);
        } else {
            const dropIndicatorBottomParent = document.querySelector('.dropIndicator-bottom')?.closest('.content-box');
            if (dropIndicatorBottomParent) {
                if(params?.position === ElementPosition.Top) {
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, editorinstance, dropIndicatorBottomParent);
                }else{
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, editorinstance, dropIndicatorBottomParent.nextSibling);
                }
            }
        }
        that.globalService.initEditor({ uuid: uuid });
        that.globalService.contentBoxSort();
        return uuid;
    }

    /**
     * content box zone where it will be add right/left
     * @param position 
     */
    contentBoxZone(position) {
        const that = this.allowedSelf();
        const uuid = this.uuid();
        const contentBoxElement = document.createElement('div') as HTMLElement;
        contentBoxElement.className = 'rowItem';
        contentBoxElement.innerHTML = `${that.PopOver.listingPopover({ classType: 'content-box', uuid: uuid })}
            <div class="content-box-inner" editor-uuid="${uuid}" type="content box"><div class="editor${uuid}" editor-uuid="${uuid}" contenteditable="false" cke-placeholder="${this._translocoService.translate("Content block html text on content-box (Template/Document - section) - Type / for quick actions")}"></div></div>`;
        if (position === ElementPosition.Left) {
            const contentBoxRow = document.querySelector('.content-box-enter').querySelector('.content-box-row');
            if (contentBoxRow) {
                this.renderer.insertBefore(contentBoxRow, contentBoxElement, contentBoxRow.firstElementChild);
            }
        } else if (position === ElementPosition.Right) {
            const contentBoxEnter = document.querySelector('.content-box-enter');
            const contentBoxRow = contentBoxEnter?.querySelector('.content-box-row');
            if (contentBoxRow) {
                this.renderer.appendChild(contentBoxRow, contentBoxElement);
            }
        }
        this.globalService.initEditor({ uuid: uuid });
        return uuid;
    }

    /**
     * detect the content box zone where it will be add right/left
     * @param params 
     */
    addContentBox(params) {
        let uuid = 0;
        if (params.position === ElementPosition.Left || params.position === ElementPosition.Right) {
            uuid = this.contentBoxZone(params.position);
        } else {
            uuid = this.createContentBox({position:params.position});
        }
        return uuid;
    }

    /**
     * return the page break view
     */
    pageBreakView(params): HTMLElement {
        const uuid = params.uuid;
        const pageBreak = document.createElement('div');
        pageBreak.className = 'content-box page-break-block marginminus70';
        pageBreak.setAttribute('break', "1");
        pageBreak.innerHTML = `<div class="content-box-row"> <div class="rowItem">
                ${this.PopOver.listingPopover({ classType: 'page-break', uuid: uuid, contentType: 'settings' })}
                <div class="sort-helper"></div>
            <div class="add-longpage-break page-break content-box-inner" uuid="${uuid}" type="page-break"></div>
            </div></div>`
        return pageBreak;
    }

    /**
     * add page break from slash command or on drag & drop
     * @param params 
     */
    addPageBreak(params) {
        if (!document.querySelector('.dropIndicator-bottom') && document.querySelector('.template-page')?.classList.contains('addContentBox') && params?.type !== 'slashcommand') {
            this.renderer.appendChild(document.querySelector('.template-page'), this.pageBreakView(params));
        } else if (params.editor && params?.type === 'slashcommand') {
            params.editor.view.dom.closest('.content-box').insertAdjacentElement('afterend', this.pageBreakView(params));
        } else {
            const dropIndicatorBottomParent = document.querySelector('.dropIndicator-bottom')?.closest('.content-box');
            if (dropIndicatorBottomParent) {
                this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, this.pageBreakView(params), dropIndicatorBottomParent.nextSibling);
            }
        }
    }

    /**
     * add attachment view in editor
     */
    attachmentBoxView(params): HTMLElement {
        const roleText = this._translocoService.translate("Attachment settings Role can upload tooltip (Template/Document - section) - Role can upload", { roleName: this._translocoService.translate("Field setting sender text (Template/Document - section) - Sender") });
        const uploadText = this._translocoService.translate("Content block html text on attachment (Template/Document - section) - Upload");
        const attachmentText = this._translocoService.translate("Content block html text on attachment (Template/Document - section) - attachment");
        const attachmentBlock = document.createElement('div');
        attachmentBlock.className = "content-box content-box-block attachment-block marginminus70";
        attachmentBlock.setAttribute("type", "attachment");
        attachmentBlock.innerHTML = `<div class="sort-helper"></div>
            <div class="content-box-row" hover-content="${roleText}">
                <div class="rowItem attachmentblock">
                    ${this.PopOver.listingPopover({ classType: 'attachment', uuid: params.uuid, contentType: 'settings' })}
                    <div class="content-box-inner attachment-box-inner attachment-block-upload editor${params.uuid}" uuid="${params.uuid}" type="attachment">
                        <div class="attachment-boxInner attachmentBox attachment-before-upload">
                            <input type="file" name="${params.uuid}" class="hidden attachment-uploader"/>
                            ${feather.icons.upload.toSvg({ class: 'cursor-pointer feather-upload me-75', width: 20, height: 20 })}
                            <span>${uploadText} <span class="attachment-name">${attachmentText}</span></span>
                        </div>
                        <div class="attachment-list-items-container attachment-after-upload hidden">
                            <div class="attachment-list-items">
                            </div>
                        </div>
                    </div>
                </div>
            </div>`
        return attachmentBlock;
    }

    /**
     * add attachment from slash command or on drag & drop
     * @param params 
     */
    addAttachmentBox(params) {
        if (!document.querySelector('.dropIndicator-bottom') && document.querySelector('.template-page')?.classList.contains('addContentBox') && params?.type !== 'slashcommand') {
            this.renderer.appendChild(document.querySelector('.template-page'), this.attachmentBoxView(params));
        } else if (params.editor && params?.type === 'slashcommand') {
            params.editor.view.dom.closest('.content-box').insertAdjacentElement('afterend', this.attachmentBoxView(params));
        } else {
            const dropIndicatorBottomParent = document.querySelector('.dropIndicator-bottom')?.closest('.content-box');
            if(dropIndicatorBottomParent){
                if(params?.position === ElementPosition.Top) {
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, this.attachmentBoxView(params), dropIndicatorBottomParent);
                }else{
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, this.attachmentBoxView(params), dropIndicatorBottomParent.nextSibling);
                }
            }
        }
    }

    attachmentLinkView(file, uuid: string, attacheduuid: string, attachment_name, isDeleteBtn: boolean = true) {
        let deleteBtn = '';
        if(isDeleteBtn){
            deleteBtn = `<span class="delete-attachment-file" uuid="${attacheduuid}" filename="${file.filename}">${feather.icons.trash.toSvg({ class: 'cursor-pointer feather-upload me-75', width: 20, height: 20 })}</span>`;
        }
        return `<div class="attachment-boxInner attachmentBox">
                ${feather.icons.paperclip.toSvg({ class: 'cursor-pointer feather-upload me-75', width: 20, height: 20 })}
                <a class="attachment-name" href="${file?.attachmentUrl}" target="_blank" temp_uuid="${uuid}" uuid="${attacheduuid}">${attachment_name}</a>
                ${deleteBtn}
        </div>`;
    }

    /**
     * add image box in editor
     * @param params 
     */
    imageBoxView(params):HTMLElement {
        const uuid = params.uuid;
        const imageBox = document.createElement('div')
        imageBox.className = "content-box content-box-block image-block marginminus70";
        imageBox.setAttribute("type","image");
        imageBox.innerHTML = `<div class="sort-helper"></div>
                    <div class="content-box-row">
                            <div class="rowItem image">
                            ${this.PopOver.imageBoxListPopover({ type: 'image', uuid: uuid })}
                            <div class="content-box-inner image-box-inner upload-image-modal-setting editor${uuid}" uuid="${uuid}" type="image">
                                <div class="image-boxInner uploadImageBox">
                                    ${feather.icons['image'].toSvg({ class: 'cursor-pointer feather-upload me-75', width: 18, height: 18 })}
                                    <span>${this._translocoService.translate("Content block html text on image box (Template/Document - section) - Click to add an Image")}</span>
                                </div>
                            </div>
                        </div>
                    </div>`
        return imageBox;
    }

    /**
     * add image box html in editor
     * @param params 
     */
    imageBoxZone(params): HTMLElement {
        const uuid = params.uuid;
        const imageBox = document.createElement('div');
        imageBox.className = "rowItem";
        imageBox.innerHTML = `${this.PopOver.imageBoxListPopover({ type: 'image', uuid: uuid })}
                    <div class="content-box-inner image-box-inner upload-image-modal-setting" uuid="${uuid}" type="image">
                        <div class="image-boxInner uploadImageBox">
                            ${feather.icons['image'].toSvg({ class: 'cursor-pointer feather-upload me-75', width: 18, height: 18 })}
                            <span>${this._translocoService.translate("Content block html text on image box (Template/Document - section) - Click to add an Image")}</span>
                        </div>
                    </div>`
        return imageBox;
    }

    /**
     * detect where image box will be add
     * @param params 
     */
    addImageBox(params) {
        if (params.position === ElementPosition.Left) {
            const contentBoxRow = document.querySelector('.content-box-enter').querySelector('.content-box-row');
            if (contentBoxRow) {
                this.renderer.insertBefore(contentBoxRow, this.imageBoxZone(params), contentBoxRow.firstElementChild);
            }
        } else if (params.position === ElementPosition.Right) {
            const contentBoxRow = document.querySelector('.content-box-enter').querySelector('.content-box-row');
            if (contentBoxRow) {
                this.renderer.appendChild(contentBoxRow, this.imageBoxZone(params));
            }
        } else {
            const indicator = document.querySelector('.content-box-enter')?.querySelector('[class^="dropIndicator-"]') as HTMLElement;
            const templatePage = document.querySelector('.template-page:not([hidden])');
            if (!document.querySelector('.dropIndicator-bottom') && templatePage?.classList.contains('addContentBox')) {
                this.renderer.appendChild(templatePage, this.imageBoxView(params));
            }else if(indicator?.classList?.contains('dropIndicator-inner-bottom')){
                this.globalService.onDropOnInnerIndicator(indicator, this.imageBoxView(params));
            } else {
                const dropIndicatorBottomParent = document.querySelector('.dropIndicator-bottom')?.closest('.content-box');
                if(dropIndicatorBottomParent){
                    if(params.position === ElementPosition.Top) {
                        this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, this.imageBoxView(params), dropIndicatorBottomParent);
                    }else{
                        this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, this.imageBoxView(params), dropIndicatorBottomParent.nextSibling);
                    }
                }
            }
        }
    }


    /**
     * add pdf block view html in editor
     * @param params 
     */
    pdfViewBlock(params) {
        const uuid = this.uuid();
        const pdfView = `<div class="content-box content-box-block pdf-block" style="--pdf-background-image:url('${params?.pdfUrl}')" parent_uuid="${params.parent_uuid ?? 0}" uuid="${uuid}"><div class="sort-helper"></div>
        <div class="content-box-row">
        <div class="rowItem pdfblock">
        ${this.PopOver.listingPopover({ classType: 'pdf-block', uuid: uuid })}
        <div class="content-box-inner" editor-uuid="${uuid}" type="PDF upload"><div class="editor${uuid} cke-editor-placeholder cke-editor-box pdf-block-editor-box pdf-height" editor-uuid="${uuid}" contenteditable="${params.contentable}">
        </div>
        </div>
        </div>
        </div></div>`;

        return { pdfView: pdfView, uuid: uuid };
    }

    pdfToHtml(params) {
        const uuid = this.uuid();
        const pdfView = `<div class="content-box content-box-block pdf-block" uuid="${uuid}"><div class="sort-helper"></div>
        <div class="content-box-row">
        <div class="rowItem">
        ${this.PopOver.listingPopover({ classType: 'pdf-block', uuid: uuid })}
        <div class="content-box-inner" editor-uuid="${uuid}" type="PDF upload"><div class="editor${uuid} cke-editor-placeholder cke-editor-box pdf-block-editor-box pdf-height" editor-uuid="${uuid}" contenteditable="${params.contentable}" style="position:relative;">
            ${params.html}
        </div>
        </div>
        </div>
        </div></div>`;

        return { pdfView: pdfView, uuid: uuid };
    }

    /**
     * add block uploaded in editor
     * @param params 
     */
    pdfBlockUploader(params) {
        const roleText = this._translocoService.translate("Attachment settings Role can upload tooltip (Template/Document - section) - Role can upload", { roleName: this._translocoService.translate("Field setting sender text (Template/Document - section) - Sender") });
        const uploadText = this._translocoService.translate("Content block html text on attachment (Template/Document - section) - Upload");
        const pdfView = document.createElement('div');
        pdfView.className = "content-box content-box-block upload-pdf-block marginminus70";
        pdfView.setAttribute("type","upload-pdf-block");
        pdfView.innerHTML =  `<div class="sort-helper"></div>
        <div class="content-box-row" hover-content="${roleText}">
            <div class="rowItem pdfblock">
                ${this.PopOver.listingPopover({ classType: 'upload-pdf-block', uuid: params.uuid, contentType: 'settings' })}
                <div class="content-box-inner attachment-box-inner pdf-block-upload editor${params.uuid}" uuid="${params.uuid}" type="Upload pdf">
                    <div class="attachment-boxInner attachmentBox attachment-before-upload">
                        <input type="file" name="${params.uuid}" class="hidden pdf-block-uploader" accept="application/pdf"/> ${feather.icons.upload.toSvg({ class: 'cursor-pointer feather-upload me-75', width: 20, height: 20 })}
                        <span>${uploadText} <span class="attachment-name">PDF</span></span>
                    </div>
                </div>
            </div>
        </div>`;
        if (!document.querySelector('.dropIndicator-bottom') && document.querySelector('.template-page')?.classList.contains('addContentBox') && params?.type !== 'slashcommand') {
            this.renderer.appendChild(document.querySelector('.template-page'), pdfView);
        }else if (params.editor && params?.type === 'slashcommand') {
            params.editor.view.dom.closest('.content-box').insertAdjacentElement('afterend', pdfView);
        }else {
            const dropIndicatorBottomParent = document.querySelector('.dropIndicator-bottom')?.closest('.content-box');
            if (dropIndicatorBottomParent) {
                if(params?.position === ElementPosition.Top) {
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, pdfView, dropIndicatorBottomParent);
                }else{
                    this.renderer.insertBefore(dropIndicatorBottomParent.parentNode, pdfView, dropIndicatorBottomParent.nextSibling);
                }
            }
        }
    }

    pdfBlockAfterUpload(params) {
        return `<div class="content-box content-box-block upload-pdf-block marginminus70" type="upload-pdf-block">
            <div class="sort-helper"></div>
            <div class="content-box-row" hover-content="${params.hoverContent}">
                <div class="rowItem pdfblock">
                    <div class="content-box-inner attachment-box-inner editor${params.uuid}" uuid="${params.uuid}" type="Upload pdf">
                        <div class="attachment-boxInner attachmentBox attachment-before-upload">
                            <input type="file" name="${params.uuid}" class="hidden pdf-block-uploader" accept="application/pdf"/> 
                            <div class="before-upload-pdf hidden">
                                ${feather.icons.upload.toSvg({ class: 'cursor-pointer feather-upload me-75', width: 20, height: 20 })}
                                <span>${this._translocoService.translate("Content block html text on attachment (Template/Document - section) - Upload")} <span class="attachment-name">PDF</span></span>
                            </div>
                            <div class="after-upload-pdf">
                                ${feather.icons.trash.toSvg({ class: 'cursor-pointer feather-upload me-75 remove-pdf-block', width: 20, height: 20, uuid: params.uuid })}
                                |
                                ${feather.icons['refresh-ccw'].toSvg({ class: 'pdf-block-upload cursor-pointer feather-upload ms-75', width: 20, height: 20, uuid: params.uuid })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>`;
    }

    /**
     * return the convert to table html in editor
     * @param params 
     */
    convertToTable(params) {
        return `<table class="cke_show_border general-convert-to-table table-border-solid" style="table-layout:auto; width: 100%;" header-color="rgb(189, 189, 189)" footer-color="rgb(255, 255, 255)" even-color="rgb(255, 255, 255)" odd-color="rgb(243, 243, 243)" activated-theme="0" border-style="1" text-color="#000000" has-header="1" has-footer="0" border-color="#d3d3d3" border-radius="0" uuid="${params.uuid}" secondary-footer-color="rgb(222, 222, 222)">
                    <thead>
                        <tr class="table_headingsRow" style="background-color: rgb(189, 189, 189);height: 40px;">
                            <th style="font-weight: normal;"></th>
                            <th style="font-weight: normal;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style="height: 40px;">
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>`;
    }

    /**
     * add content library box
     * @param params 
     */
    contentLibraryView(params) {
        const uuid = params.uuid;
        return `
            <div class="sort-helper"></div>
            <div class="content-box-row">
                <div class="rowItem">
                    ${this.PopOver.listingPopover({ classType: 'content-library', uuid: uuid })}
                    <div class="content-box-inner image-box-inner content-library-modal-setting" uuid="${uuid}" type="content library">
                        <div class="image-boxInner uploadImageBox">
                            ${feather.icons['image'].toSvg({ class: 'cursor-pointer feather-upload me-75', width: 18, height: 18 })}
                            <span>${this._translocoService.translate("Content block html text on image box (Template/Document - section) - Click to add content library")}</span>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    insertContentLibrary(params) {
        const contentLibrary = this.renderer.createElement('div') as HTMLElement;
        this.renderer.addClass(contentLibrary, 'content-box');
        this.renderer.addClass(contentLibrary, 'content-box-block');
        this.renderer.addClass(contentLibrary, 'content-library-block');
        this.renderer.addClass(contentLibrary, 'marginminus70');
        this.renderer.setAttribute(contentLibrary, 'type', 'content-library');
        contentLibrary.innerHTML = this.contentLibraryView(params);
        const dropIndicator = document.querySelector('.dropIndicator-bottom');
        const parentContentBox = dropIndicator?.closest('.content-box');
        const templatePage = document.querySelector('.template-page');
        if (!dropIndicator && templatePage.classList.contains('addContentBox')) {
            this.renderer.appendChild(templatePage, contentLibrary);
        } else {
            if(parentContentBox){
                if(params?.position === ElementPosition.Top) {
                    this.renderer.insertBefore(parentContentBox.parentNode, contentLibrary, parentContentBox);
                }else{
                    this.renderer.insertBefore(parentContentBox.parentNode, contentLibrary, parentContentBox.nextSibling);
                }
            }
        }
    }
}
